import { useCallback, useRef, useState } from 'react';
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { toast } from 'sonner';

/**
 * Generic optimistic mutation helper.
 *
 * Implements the canonical TanStack Query optimistic pattern:
 *  - `onMutate`  → run `applyOptimistic` (local state) and/or patch the TQ cache
 *  - `onError`   → call `rollback` (or restore the cached snapshot) + toast.error
 *  - `onSuccess` → toast.success with the configured message
 *  - `onSettled` → invalidate the configured query key
 *
 * It additionally tracks "in-flight" item keys so the UI can render a
 * per-row "Guardando…" indicator (`isPending(key)`).
 *
 * The hook works both with components that own data in local state
 * (passing `applyOptimistic` / `rollback`) and with components that read
 * from the TanStack Query cache (passing `queryKey`).
 */
export interface UseOptimisticMutationOptions<TVars, TData = unknown, TCache = unknown> {
  /** The mutation function. Required. */
  mutationFn: (vars: TVars) => Promise<TData> | TData;
  /** Stable key for the affected item (used to track in-flight state). */
  getKey?: (vars: TVars) => string;
  /** Optionally mutate caller-owned local state. */
  applyOptimistic?: (vars: TVars) => void;
  /** Restore caller-owned local state if the mutation fails. */
  rollback?: (vars: TVars, error: unknown) => void;
  /** When provided, the TQ cache for that key is snapshotted/restored automatically. */
  queryKey?: QueryKey;
  /** Optional patch applied to the cached value when `queryKey` is provided. */
  patchCache?: (prev: TCache | undefined, vars: TVars) => TCache | undefined;
  /** Message shown on success. Set to `null` to disable the toast. */
  successMessage?: string | ((vars: TVars, data: TData) => string) | null;
  /** Message shown on error. Set to `null` to disable the toast. */
  errorMessage?: string | ((vars: TVars, error: unknown) => string) | null;
  /** Invalidate the query key after settle. Defaults to `true` when `queryKey` is set. */
  invalidateOnSettle?: boolean;
}

export function useOptimisticMutation<TVars, TData = unknown, TCache = unknown>(
  options: UseOptimisticMutationOptions<TVars, TData, TCache>,
) {
  const {
    mutationFn,
    getKey,
    applyOptimistic,
    rollback,
    queryKey,
    patchCache,
    successMessage,
    errorMessage = 'No se pudo guardar el cambio',
    invalidateOnSettle,
  } = options;

  const queryClient = useQueryClient();
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
  const snapshotsRef = useRef<Map<string, TCache | undefined>>(new Map());

  const addPending = useCallback((key: string) => {
    setPendingKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);
  const removePending = useCallback((key: string) => {
    setPendingKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const mutation = useMutation({
    mutationFn: async (vars: TVars) => mutationFn(vars),
    onMutate: async (vars) => {
      const key = getKey?.(vars);
      if (key) addPending(key);

      if (queryKey) {
        await queryClient.cancelQueries({ queryKey });
        const snapshot = queryClient.getQueryData<TCache>(queryKey);
        if (key) snapshotsRef.current.set(key, snapshot);
        if (patchCache) {
          queryClient.setQueryData<TCache>(queryKey, (prev) => patchCache(prev, vars));
        }
      }

      applyOptimistic?.(vars);
      return { key } as { key?: string };
    },
    onError: (error, vars, ctx) => {
      const key = ctx?.key;
      if (queryKey && key && snapshotsRef.current.has(key)) {
        queryClient.setQueryData(queryKey, snapshotsRef.current.get(key));
      }
      rollback?.(vars, error);
      if (errorMessage !== null) {
        const msg = typeof errorMessage === 'function' ? errorMessage(vars, error) : errorMessage;
        toast.error(msg);
      }
    },
    onSuccess: (data, vars) => {
      if (successMessage !== null && successMessage !== undefined) {
        const msg = typeof successMessage === 'function' ? successMessage(vars, data) : successMessage;
        if (msg) toast.success(msg);
      }
    },
    onSettled: (_data, _err, vars, ctx) => {
      const key = ctx?.key;
      if (key) {
        snapshotsRef.current.delete(key);
        removePending(key);
      }
      const shouldInvalidate = invalidateOnSettle ?? Boolean(queryKey);
      if (shouldInvalidate && queryKey) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  const isPending = useCallback((key: string) => pendingKeys.has(key), [pendingKeys]);

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isPendingFor: isPending,
    pendingKeys,
  };
}
